import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Plus, FolderOpen, Trash2, Loader2, AlertTriangle } from 'lucide-react';
import { formatRelativeTime } from '@/lib/utils';
import { useProjects, useCreateProject, useDeleteProject } from '@/hooks/use-projects';
import { useMe } from '@/hooks/use-me';
import { toast } from '@/hooks/use-toast';
import { PlanLimitError, planLimitMessage, allowanceLabel, CONTACT_PATH } from '@/lib/plan-limit';

const PLAN_NAMES: Record<string, string> = { base: 'Base', internal: 'Internal' };

function RequestAccess({ email }: { email: string }) {
  return (
    <div className="container max-w-5xl py-8">
      <div className="border rounded-lg p-10 bg-card max-w-xl mx-auto space-y-4">
        <h1 className="text-2xl font-bold tracking-tight">You're signed in, but not on the list yet</h1>
        <p className="text-muted-foreground">
          ValueGraph is invite-only while we work with a small first group. You're signed in as{' '}
          <span className="font-medium text-foreground">{email}</span>. Ask us for access and we'll set your account up.
        </p>
        <Button variant="brand" asChild>
          <a href={CONTACT_PATH}>Ask for access</a>
        </Button>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const { data, isLoading, isError, isFetching, refetch } = useProjects();
  const projects = data ?? [];
  const me = useMe();
  const createProject = useCreateProject();
  const deleteProject = useDeleteProject();
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  const account = me.data?.account ?? null;
  // Account known + absent + no shared projects → request-access page. A
  // failed /me is "unknown": show the dashboard and let the server gate.
  const notOnList = me.isSuccess && account === null;
  const projectLimit = account?.limits.projects ?? null;
  const atProjectLimit = !!account && projectLimit !== null && account.usage.projects >= projectLimit;
  const createBlockedReason = notOnList
    ? "Your account isn't set up to create projects yet."
    : atProjectLimit
      ? `Your plan includes ${projectLimit} ${projectLimit === 1 ? 'project' : 'projects'}. Deleting one doesn't free the space.`
      : null;
  const planLine = account ? allowanceLabel(account.usage.projects, projectLimit, 'project', 'projects') : null;

  const resetForm = () => {
    setName('');
    setDescription('');
  };

  const handleCreate = async () => {
    if (!name.trim()) return;
    try {
      const project = await createProject.mutateAsync({
        name: name.trim(),
        description: description.trim() || undefined,
      });
      resetForm();
      setShowForm(false);
      navigate(`/project/${project.id}`);
    } catch (err) {
      if (err instanceof PlanLimitError) {
        const m = planLimitMessage(err.payload);
        toast({ title: m.title, description: m.description, variant: 'destructive' });
      } else {
        toast({
          title: "Couldn't create the project",
          description: err instanceof Error ? err.message : '',
          variant: 'destructive',
        });
      }
    }
  };

  const handleDelete = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setPendingDeleteId(id);
  };

  if (isLoading || me.isLoading) {
    return (
      <div className="container max-w-5xl py-8 flex justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Only block when there's nothing cached to fall back on. React Query keeps
  // the last good data and still flips isError when a background refetch fails
  // (focus refetch past staleTime, or a mutation's invalidate), and replacing a
  // loaded list with an error screen on a transient blip is worse than showing
  // a slightly stale one. `data` is undefined only if no load ever succeeded —
  // a genuine empty list is [], which correctly falls through to the empty state.
  if (isError && data === undefined) {
    return (
      <div className="container max-w-5xl py-8">
        <div className="rounded-lg border bg-card p-5 flex items-center gap-3 text-sm">
          <AlertTriangle className="h-4 w-4 shrink-0 text-destructive" />
          <span className="text-destructive">
            Couldn't load your projects. Nothing's lost — check your connection and try again.
          </span>
          <Button
            variant="outline"
            size="sm"
            className="ml-auto shrink-0"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            {isFetching ? 'Retrying...' : 'Try again'}
          </Button>
        </div>
      </div>
    );
  }

  if (notOnList && projects.length === 0 && me.data) {
    return <RequestAccess email={me.data.email} />;
  }

  return (
    <div className="container max-w-5xl py-8 space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Projects</h1>
          {planLine && account && (
            <p className="text-sm text-muted-foreground mt-1">
              {PLAN_NAMES[account.plan] ?? account.plan} plan · {planLine}
            </p>
          )}
        </div>
        <div className="flex flex-col items-end gap-1">
          <Button variant="brand" onClick={() => setShowForm(true)} disabled={createBlockedReason !== null}>
            <Plus className="h-4 w-4 mr-2" />
            New Project
          </Button>
          {createBlockedReason && (
            <p className="text-xs text-muted-foreground text-right max-w-xs">
              {createBlockedReason}{' '}
              <a href={CONTACT_PATH} className="underline underline-offset-2">
                Talk to us
              </a>
            </p>
          )}
        </div>
      </div>

      {showForm && (
        <div className="border rounded-lg p-6 bg-card space-y-4">
          <h2 className="text-lg font-semibold">Create New Project</h2>
          <div className="space-y-3">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Project name"
              className="w-full px-3 py-2 border rounded-md bg-background focus:outline-hidden focus:ring-2 focus:ring-ring"
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            />
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Description (optional)"
              className="w-full px-3 py-2 border rounded-md bg-background focus:outline-hidden focus:ring-2 focus:ring-ring resize-none"
              rows={2}
            />
          </div>
          <div className="flex gap-2">
            <Button onClick={handleCreate} disabled={!name.trim() || createProject.isPending}>
              {createProject.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Create
            </Button>
            <Button variant="outline" onClick={() => { setShowForm(false); resetForm(); }}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {projects.length === 0 && !showForm ? (
        <div className="border rounded-lg p-12 text-center bg-card">
          <FolderOpen className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-lg font-semibold mb-2">No projects yet</h2>
          <p className="text-muted-foreground mb-4">
            Create your first value proposition project to get started.
          </p>
          <Button variant="brand" onClick={() => setShowForm(true)} disabled={createBlockedReason !== null}>
            <Plus className="h-4 w-4 mr-2" />
            Create Project
          </Button>
        </div>
      ) : (
        <div className="grid gap-4">
          {projects.map((project) => (
            <div
              key={project.id}
              className="border rounded-lg p-4 bg-card hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => navigate(`/project/${project.id}`)}
            >
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <h3 className="font-semibold text-lg">{project.name}</h3>
                  {project.description && (
                    <p className="text-sm text-muted-foreground">{project.description}</p>
                  )}
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span>Updated {formatRelativeTime(project.updatedAt)}</span>
                    {project.callerRole && project.callerRole !== 'owner' && (
                      <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700">
                        Shared · {project.callerRole}
                      </span>
                    )}
                  </div>
                </div>
                {/* Only the owner can delete — matches the backend owner-only gate. */}
                {project.callerRole === 'owner' && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-muted-foreground hover:text-destructive"
                    onClick={(e) => handleDelete(project.id, e)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={pendingDeleteId !== null}
        title="Delete this project?"
        body="This removes the project and everything in it, and can't be undone. It doesn't free up your project allowance."
        confirmLabel="Delete project"
        onConfirm={() => {
          if (pendingDeleteId) deleteProject.mutate(pendingDeleteId);
          setPendingDeleteId(null);
        }}
        onCancel={() => setPendingDeleteId(null)}
      />
    </div>
  );
}
